import React, { useEffect, useState, useRef } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import '../utils/glyph-page.css';

export default function Glyph2Page() {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [circleGrid, setCircleGrid] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showImage, setShowImage] = useState(true);
  const [imageVisible, setImageVisible] = useState(false);
  const [imageOpacity, setImageOpacity] = useState(0);
  const [contrast, setContrast] = useState(1.0);
  const [minRadius, setMinRadius] = useState(1.0);
  const [maxRadius, setMaxRadius] = useState(5.5);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingBlob, setRecordingBlob] = useState(null);
  const [isCapturingFrames, setIsCapturingFrames] = useState(false);
  const [capturedFrames, setCapturedFrames] = useState([]);
  const [recordingMode, setRecordingMode] = useState(false);
  const animationStartTimeRef = useRef(null);
  const [canvasStyle, setCanvasStyle] = useState({});

  const canvasRef = useRef(null);
  const activeImageRef = useRef(null);
  const animationRef = useRef(null);
  const circleStateRef = useRef(null); // Track current circle state like glyphStateRef
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const capturedFramesRef = useRef([]);
  const isCapturingFramesRef = useRef(false);
  const isRecordingSessionRef = useRef(false);

  const CELL_SIZE = 10;
  const CIRCLE_STEPS = 10;
  const CROSSFADE_DURATION = 400;
  const ANIMATION_SPEED = 40;

  // Title management
  usePageTitle({
    title: 'ITORA - Circle Animation',
    description: 'Upload an image and watch it transform into animated circles using our AI-powered visualization technique.',
    url: `${window.location.origin}/glyph2`
  });

  // Load test image by default
  useEffect(() => {
    setImageUrl('/img/abtest/a.png');
  }, []);

  // Update canvas size and position to match the active image
  useEffect(() => {
    if (!activeImageRef.current || !canvasRef.current) return;
    const img = activeImageRef.current;
    const canvas = canvasRef.current;
    
    // Wait for image to load and get its natural dimensions
    if (img.complete && img.naturalWidth > 0) {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      canvas.width = width;
      canvas.height = height;
      setCanvasStyle({
        position: 'absolute',
        top: 0,
        left: 0,
        width: img.offsetWidth,
        height: img.offsetHeight,
        pointerEvents: 'none',
        zIndex: 5
      });
    } else {
      // If image not loaded yet, wait for it
      const handleLoad = () => {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        canvas.width = width;
        canvas.height = height;
        setCanvasStyle({
          position: 'absolute',
          top: 0,
          left: 0,
          width: img.offsetWidth,
          height: img.offsetHeight,
          pointerEvents: 'none',
          zIndex: 5
        });
      };
      
      img.addEventListener('load', handleLoad);
      return () => img.removeEventListener('load', handleLoad);
    }
  }, [imageUrl]);

  // Draw circles function
  const drawCircles = (grid) => {
    if (!grid || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    
    if (width === 0 || height === 0) return;
    
    ctx.clearRect(0, 0, width, height);
    
    // Fill with white background when recording
    if (isRecording || isRecordingSessionRef.current) {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, width, height);
    }
    
    for (let y = 0; y < grid.length; y++) {
      for (let x = 0; x < grid[0].length; x++) {
        const step = grid[y][x];
        if (step === null || step === undefined || step === 0) continue; // Skip step 0 (invisible)
        
        const radius = minRadius + (maxRadius - minRadius) * (step / (CIRCLE_STEPS - 1));
        if (radius <= 0.1) continue;
        
        ctx.beginPath();
        ctx.arc(x * CELL_SIZE + CELL_SIZE / 2, y * CELL_SIZE + CELL_SIZE / 2, radius, 0, 2 * Math.PI);
        ctx.fillStyle = '#3520D6';
        ctx.globalAlpha = 0.9;
        ctx.fill();
        ctx.globalAlpha = 1.0;
      }
    }
  };

  // Process image with current settings for live preview
  const processImageForPreview = () => {
    if (!imageUrl || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
    
    const tryProcess = () => {
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      if (canvasWidth === 0 || canvasHeight === 0 || img.width === 0 || img.height === 0) {
        setTimeout(tryProcess, 30);
        return;
      }

      const imgAspect = img.width / img.height;
      const canvasAspect = canvasWidth / canvasHeight;

      let drawWidth, drawHeight, offsetX, offsetY;
      if (imgAspect > canvasAspect) {
        drawHeight = canvasHeight;
        drawWidth = img.width * (canvasHeight / img.height);
        offsetX = (canvasWidth - drawWidth) / 2;
        offsetY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = img.height * (canvasWidth / img.width);
        offsetX = 0;
        offsetY = (canvasHeight - drawHeight) / 2;
      }

      const offscreen = document.createElement('canvas');
      offscreen.width = canvasWidth;
      offscreen.height = canvasHeight;
      const offCtx = offscreen.getContext('2d');
      offCtx.imageSmoothingEnabled = false;
      offCtx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      const imageData = offCtx.getImageData(0, 0, canvasWidth, canvasHeight).data;
      
      // Create grid with current contrast setting
      const grid = [];
      for (let y = 0; y < canvasHeight; y += CELL_SIZE) {
        const row = [];
        for (let x = 0; x < canvasWidth; x += CELL_SIZE) {
          const i = (y * canvasWidth + x) * 4;
          let brightness = (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3;
          brightness = ((brightness / 255 - 0.5) * contrast + 0.5) * 255;
          brightness = Math.max(0, Math.min(255, brightness));
          const step = Math.round((1 - brightness / 255) * (CIRCLE_STEPS - 1));
          row.push(step);
        }
        grid.push(row);
      }
      
      // Update the grid and redraw with current dot sizes
      setCircleGrid(grid);
    };

    img.onload = tryProcess;
  };

  // Direct Canvas Animation
  const startAnimation = () => {
    if (!imageUrl || !canvasRef.current) return;
    
    console.log('startAnimation called - PLAY button pressed at', new Date().toISOString());
    
    // PLAY button should completely reset all recording state
    setRecordingMode(false);
    setRecordingBlob(null);
    setCapturedFrames([]);
    setIsRecording(false);
    setIsCapturingFrames(false);
    isCapturingFramesRef.current = false;
    isRecordingSessionRef.current = false; // Clear recording session ref
    
    // Clear any existing recording state
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
    }
    if (capturedFramesRef.current) {
      capturedFramesRef.current = [];
    }
    
    // Reset everything
    setImageVisible(false);
    setImageOpacity(0);
    setCircleGrid(null);
    animationStartTimeRef.current = Date.now(); // Track start time
    
    if (animationRef.current) {
      clearInterval(animationRef.current);
      animationRef.current = null;
    }
    
    // Clear canvas
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    
    const canvas = canvasRef.current;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
    
    const tryProcess = () => {
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      if (canvasWidth === 0 || canvasHeight === 0 || img.width === 0 || img.height === 0) {
        setTimeout(tryProcess, 30);
        return;
      }

      const imgAspect = img.width / img.height;
      const canvasAspect = canvasWidth / canvasHeight;

      let drawWidth, drawHeight, offsetX, offsetY;
      if (imgAspect > canvasAspect) {
        drawHeight = canvasHeight;
        drawWidth = img.width * (canvasHeight / img.height);
        offsetX = (canvasWidth - drawWidth) / 2;
        offsetY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = img.height * (canvasWidth / img.width);
        offsetX = 0;
        offsetY = (canvasHeight - drawHeight) / 2;
      }

      const offscreen = document.createElement('canvas');
      offscreen.width = canvasWidth;
      offscreen.height = canvasHeight;
      const offCtx = offscreen.getContext('2d');
      offCtx.imageSmoothingEnabled = false;
      offCtx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      const imageData = offCtx.getImageData(0, 0, canvasWidth, canvasHeight).data;
      
      // Create grid
      const grid = [];
      for (let y = 0; y < canvasHeight; y += CELL_SIZE) {
        const row = [];
        for (let x = 0; x < canvasWidth; x += CELL_SIZE) {
          const i = (y * canvasWidth + x) * 4;
          let brightness = (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3;
          brightness = ((brightness / 255 - 0.5) * contrast + 0.5) * 255;
          brightness = Math.max(0, Math.min(255, brightness));
          const step = Math.round((1 - brightness / 255) * (CIRCLE_STEPS - 1));
          row.push(step);
        }
        grid.push(row);
      }
      
      setCircleGrid(grid);
      
      // Start with all circles at minimum size (like g11 in Glyph.jsx)
      const initialGrid = grid.map(row => row.map(() => 0));
      setCircleGrid(initialGrid);
      circleStateRef.current = initialGrid; // Set the ref to track current state
      
      // Phase 1: Animate to target sizes (like animateGlyphsToPosition)
      setTimeout(() => {
        animateToTargetSizes(grid);
      }, 100);
    };

    img.onload = tryProcess;
  };

  const startRecordingAnimation = () => {
    if (!imageUrl || !canvasRef.current) return;
    
    console.log('startRecordingAnimation called - RECORD button pressed at', new Date().toISOString());
    
    // Reset all recording state first
    setRecordingMode(false);
    setRecordingBlob(null);
    setCapturedFrames([]);
    setIsRecording(false);
    setIsCapturingFrames(false);
    isCapturingFramesRef.current = false; // Reset frame capture ref
    
    // Clear any existing recording state
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      mediaRecorderRef.current = null;
    }
    if (capturedFramesRef.current) {
      capturedFramesRef.current = [];
    }
    
    // Now set recording mode and start
    setRecordingMode(true);
    isRecordingSessionRef.current = true; // Set recording session ref
    
    console.log('Recording mode activated - starting animation and recording...');
    
    // Start animation immediately (same as PLAY button)
    setImageVisible(false);
    setImageOpacity(0);
    setCircleGrid(null);
    animationStartTimeRef.current = Date.now(); // Track start time
    
    if (animationRef.current) {
      clearInterval(animationRef.current);
      animationRef.current = null;
    }
    
    // Clear canvas
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    
    // Start recording BEFORE processing image to ensure we capture everything
    startRecording();
    
    const canvas = canvasRef.current;
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = imageUrl;
    
    const tryProcess = () => {
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      if (canvasWidth === 0 || canvasHeight === 0 || img.width === 0 || img.height === 0) {
        setTimeout(tryProcess, 30);
        return;
      }

      const imgAspect = img.width / img.height;
      const canvasAspect = canvasWidth / canvasHeight;

      let drawWidth, drawHeight, offsetX, offsetY;
      if (imgAspect > canvasAspect) {
        drawHeight = canvasHeight;
        drawWidth = img.width * (canvasHeight / img.height);
        offsetX = (canvasWidth - drawWidth) / 2;
        offsetY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = img.height * (canvasWidth / img.width);
        offsetX = 0;
        offsetY = (canvasHeight - drawHeight) / 2;
      }

      const offscreen = document.createElement('canvas');
      offscreen.width = canvasWidth;
      offscreen.height = canvasHeight;
      const offCtx = offscreen.getContext('2d');
      offCtx.imageSmoothingEnabled = false;
      offCtx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);

      const imageData = offCtx.getImageData(0, 0, canvasWidth, canvasHeight).data;
      
      // Create grid
      const grid = [];
      for (let y = 0; y < canvasHeight; y += CELL_SIZE) {
        const row = [];
        for (let x = 0; x < canvasWidth; x += CELL_SIZE) {
          const i = (y * canvasWidth + x) * 4;
          let brightness = (imageData[i] + imageData[i + 1] + imageData[i + 2]) / 3;
          brightness = ((brightness / 255 - 0.5) * contrast + 0.5) * 255;
          brightness = Math.max(0, Math.min(255, brightness));
          const step = Math.round((1 - brightness / 255) * (CIRCLE_STEPS - 1));
          row.push(step);
        }
        grid.push(row);
      }
      
      setCircleGrid(grid);
      
      // Start with all circles at minimum size (like g11 in Glyph.jsx)
      const initialGrid = grid.map(row => row.map(() => 0));
      setCircleGrid(initialGrid);
      circleStateRef.current = initialGrid; // Set the ref to track current state
      
      // Phase 1: Animate to target sizes (like animateGlyphsToPosition)
      setTimeout(() => {
        animateToTargetSizes(grid);
      }, 100);
    };

    img.onload = tryProcess;
  };

  // Animate circles to their target sizes (like animateGlyphsToPosition)
  const animateToTargetSizes = (targetGrid) => {
    const maxSteps = CIRCLE_STEPS - 1;
    let currentStep = 0;

    const animate = () => {
      if (currentStep >= maxSteps) {
        setCircleGrid(targetGrid);
        circleStateRef.current = targetGrid; // Update ref to final state
        
        // Pause, then animate back to minimum size
        setTimeout(() => {
          animateToMinimumSize(targetGrid);
        }, 250); // GLYPH_PAUSE equivalent
        
        return;
      }

      // Create a new grid where each circle grows by one step
      const newGrid = targetGrid.map((row, y) =>
        row.map((targetStep, x) => {
          // Get current step for this position (starts at 0)
          const currentStepForThisPosition = circleStateRef.current?.[y]?.[x] || 0;
          // Grow by one step, but don't exceed target
          return Math.min(currentStepForThisPosition + 1, targetStep);
        })
      );

      setCircleGrid(newGrid);
      circleStateRef.current = newGrid; // Update the ref with new state
      currentStep++;
      
      // Use setTimeout instead of requestAnimationFrame for proper timing
      setTimeout(animate, ANIMATION_SPEED);
    };

    animate();
  };

  // Animate circles back to minimum size (like animateGlyphsToG11)
  const animateToMinimumSize = (targetGrid) => {
    // Start the crossfade immediately
    setImageVisible(true);

    const maxSteps = CIRCLE_STEPS - 1;
    const stepsNeeded = Math.max(1, Math.ceil(CROSSFADE_DURATION / ANIMATION_SPEED));
    let currentStep = 0;

    const animate = () => {
      if (currentStep >= stepsNeeded) {
        // All circles at minimum size
        const minGrid = targetGrid.map(row => row.map(() => 0));
        setCircleGrid(minGrid);
        circleStateRef.current = minGrid; // Update ref to minimum state
        
        // Start random clearing
        setTimeout(() => {
          clearCircles(targetGrid);
        }, 100);
        
        return;
      }

      const progress = (currentStep + 1) / stepsNeeded;
      const newGrid = targetGrid.map((row, y) =>
        row.map((currentStepValue) => {
          const targetStep = 0;
          const stepsToTarget = currentStepValue - targetStep;
          const stepsToMove = Math.ceil(stepsToTarget * progress);
          return Math.max(currentStepValue - stepsToMove, 0);
        })
      );

      setCircleGrid(newGrid);
      circleStateRef.current = newGrid; // Update the ref with new state
      currentStep++;
      
      // Use setTimeout instead of requestAnimationFrame for proper timing
      setTimeout(animate, ANIMATION_SPEED);
    };

    animate();
  };

  // Clear circles function
  const clearCircles = (targetGrid) => {
    // Use current state instead of target grid for clearing
    const currentGrid = circleStateRef.current;
    if (!currentGrid) return;
    
    // Create a copy of the current grid to work with
    const workingGrid = currentGrid.map(row => [...row]);
    
    // Collect all positions that have circles
    const allPositions = [];
    for (let y = 0; y < workingGrid.length; y++) {
      for (let x = 0; x < workingGrid[0].length; x++) {
        if (workingGrid[y][x] !== null && workingGrid[y][x] !== undefined) {
          allPositions.push({ x, y });
        }
      }
    }
    
    // Shuffle positions for random clearing order
    for (let i = allPositions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [allPositions[i], allPositions[j]] = [allPositions[j], allPositions[i]];
    }
    
    let currentIndex = 0;
    const cellsPerStep = Math.max(1, Math.floor(allPositions.length / 50)); // Clear faster
    
    const clearTimer = setInterval(() => {
      if (currentIndex >= allPositions.length) {
        clearInterval(clearTimer);
        // Set final empty grid
        const emptyGrid = workingGrid.map(row => row.map(() => null));
        setCircleGrid(emptyGrid);
        circleStateRef.current = emptyGrid;
        
        // Both recording and regular animations should complete the same way
        const duration = Date.now() - animationStartTimeRef.current;
        console.log('Animation complete at', new Date().toISOString(), 'Duration:', duration + 'ms');
        
        // If this was a recording session, handle recording completion
        if (isRecordingSessionRef.current) {
          console.log('Recording session detected, completing recording...');
          completeRecordingAnimation();
        }
        
        return;
      }
      
      // Clear circles in batches
      for (let i = 0; i < cellsPerStep && currentIndex < allPositions.length; i++) {
        const pos = allPositions[currentIndex];
        workingGrid[pos.y][pos.x] = null;
        currentIndex++;
      }
      
      // Update the grid state
      setCircleGrid(workingGrid.map(row => [...row]));
      circleStateRef.current = workingGrid.map(row => [...row]);
    }, 10); // Faster clearing
  };

  // Recording functions from Glyph.jsx
  const completeRecordingAnimation = () => {
    console.log('Recording animation complete, stopping recording in 2 seconds...');
    
    // Stop recording after 2 seconds to capture the complete clearing animation
    setTimeout(() => {
      console.log('Stopping recording now after 2 second delay...');
      stopRecording();
      isRecordingSessionRef.current = false;
    }, 2000);
  };

  const startRecording = () => {
    if (!canvasRef.current) {
      console.warn('Canvas ref not available for recording');
      return;
    }
    
    // Prevent multiple recording sessions
    if (isRecording) {
      console.warn('Recording already in progress, skipping...');
      return;
    }
    
    const canvas = canvasRef.current;
    console.log('Starting recording, canvas dimensions:', canvas.width, 'x', canvas.height);
    
    // Only start recording if canvas has valid dimensions
    if (canvas.width === 0 || canvas.height === 0) {
      console.warn('Canvas has no dimensions, skipping recording');
      return;
    }
    
    // Check if canvas has content
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const hasContent = imageData.data.some(pixel => pixel !== 0);
    console.log('Canvas has content:', hasContent);
    
    if (!hasContent) {
      console.warn('Canvas has no content, waiting for first frame...');
      setTimeout(startRecording, 100);
      return;
    }
    
    // Capture the main canvas stream directly
    const stream = canvas.captureStream(60); // 60 FPS for smoother video
    
    // Try different MIME types for better browser compatibility, prioritizing MP4
    let mimeType = 'video/mp4';
    if (!MediaRecorder.isTypeSupported(mimeType)) {
      mimeType = 'video/webm;codecs=vp9';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'video/webm;codecs=vp8';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'video/webm';
        }
      }
    }
    
    console.log('Using MIME type:', mimeType);
    
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType: mimeType,
      videoBitsPerSecond: 8000000 // 8 Mbps for higher quality
    });
    
    recordedChunksRef.current = [];
    
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordedChunksRef.current.push(event.data);
        console.log('Recording chunk received, size:', event.data.size);
      }
    };
    
    mediaRecorder.onstop = () => {
      console.log('Recording stopped, creating blob...');
      console.log('Recorded chunks:', recordedChunksRef.current.length);
      const blob = new Blob(recordedChunksRef.current, { type: mimeType });
      console.log('Blob created, size:', blob.size, 'type:', mimeType);
      setRecordingBlob(blob);
      setIsRecording(false);
      setRecordingMode(false); // Reset recording mode after completion
      console.log('✅ Recording complete! Download buttons should now be visible.');
    };
    
    mediaRecorder.onerror = (event) => {
      console.error('MediaRecorder error:', event);
    };
    
    mediaRecorder.onstart = () => {
      console.log('MediaRecorder started successfully');
    };
    
    // Store the media recorder reference
    mediaRecorderRef.current = mediaRecorder;
    
    mediaRecorder.start();
    setIsRecording(true);
    console.log('Recording started successfully');
  };

  const stopRecording = () => {
    console.log('stopRecording called, isRecording:', isRecording, 'mediaRecorderRef.current:', !!mediaRecorderRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      console.log('Stopping recording, current state:', mediaRecorderRef.current.state);
      try {
        mediaRecorderRef.current.stop();
        console.log('Recording stop() called successfully');
      } catch (error) {
        console.error('Error stopping recording:', error);
      }
    } else {
      console.log('Recording not stopped - conditions not met');
      console.log('mediaRecorderRef.current exists:', !!mediaRecorderRef.current);
      if (mediaRecorderRef.current) {
        console.log('mediaRecorder state:', mediaRecorderRef.current.state);
      }
    }
  };

  const downloadRecording = () => {
    if (!recordingBlob) return;
    
    const url = URL.createObjectURL(recordingBlob);
    const a = document.createElement('a');
    a.href = url;
    
    // Determine file extension based on blob type
    let extension = 'mp4';
    if (recordingBlob.type.includes('webm')) {
      extension = 'webm';
    } else if (recordingBlob.type.includes('mp4')) {
      extension = 'mp4';
    }
    
    a.download = `circle-animation.${extension}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      setImageUrl(url);
      setUploadedImage(file);
      setCircleGrid(null);
      setImageVisible(false);
      setRecordingMode(false); // Reset recording mode for new image
      setRecordingBlob(null);
      setCapturedFrames([]);
      setIsRecording(false);
      setIsCapturingFrames(false);
      isCapturingFramesRef.current = false;
      isRecordingSessionRef.current = false;
      
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
    }
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        const url = URL.createObjectURL(file);
        setImageUrl(url);
        setUploadedImage(file);
        setCircleGrid(null);
        setImageVisible(false);
        setRecordingMode(false); // Reset recording mode for new image
        setRecordingBlob(null);
        setCapturedFrames([]);
        setIsRecording(false);
        setIsCapturingFrames(false);
        isCapturingFramesRef.current = false;
        isRecordingSessionRef.current = false;
        
        if (animationRef.current) {
          clearInterval(animationRef.current);
          animationRef.current = null;
        }
      }
    }
  };

  useEffect(() => {
    if (circleGrid) {
      drawCircles(circleGrid);
    }
  }, [circleGrid]);

  useEffect(() => {
    setCircleGrid(null);
    setImageVisible(false);
    if (!imageUrl) return;
    
    // Process image for display
    if (canvasRef.current && activeImageRef.current) {
      const canvas = canvasRef.current;
      const img = activeImageRef.current;
      
      if (img.complete && img.naturalWidth > 0) {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
      }
    }
  }, [imageUrl]);

  useEffect(() => {
    return () => {
      if (animationRef.current) {
        clearInterval(animationRef.current);
        animationRef.current = null;
      }
      // Clean up recording state
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  return (
    <div 
      className={`glyph-page ${isDragOver ? 'drag-over' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Top control bar */}
      <div className="glyph-top-bar">
        <div className="top-bar-content">
          <div 
            className="upload-button"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <label htmlFor="image-upload" className="upload-label">
              <span>📁 Upload Image</span>
            </label>
            <input
              id="image-upload"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
          </div>
          
          <button
            className="btn btn-play"
            onClick={startAnimation}
            disabled={!imageUrl}
          >
            ▶
          </button>
          
          <button
            className={`btn ${recordingMode ? 'btn-record-active' : 'btn-record'}`}
            onClick={startRecordingAnimation}
            disabled={!imageUrl || isRecording}
          >
            {recordingMode ? '🔴' : '⚫'}
          </button>
          
          <label className="image-toggle">
            <input
              type="checkbox"
              checked={showImage}
              onChange={(e) => setShowImage(e.target.checked)}
            />
            <span>Show Image</span>
          </label>
          
          <div className="contrast-control">
            <label>Contrast</label>
            <input
              type="range"
              min="0.1"
              max="3.0"
              step="0.1"
              value={contrast}
              onChange={(e) => {
                const newContrast = parseFloat(e.target.value);
                setContrast(newContrast);
                // Live preview - reprocess image with new contrast
                if (circleGrid) {
                  processImageForPreview();
                }
              }}
              className="contrast-slider"
            />
            <span className="contrast-value">{contrast.toFixed(1)}</span>
          </div>
          
          <div className="dot-size-control" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <label style={{ marginRight: 4 }}>Small Dot Size</label>
            <input
              type="range"
              min="0.1"
              max={maxRadius - 0.1}
              step="0.1"
              value={minRadius}
              onChange={e => {
                const newMinRadius = Math.min(parseFloat(e.target.value), maxRadius - 0.1);
                setMinRadius(newMinRadius);
                // Live preview - redraw circles with new sizes
                if (circleGrid) {
                  drawCircles(circleGrid);
                }
              }}
              style={{ width: 60 }}
            />
            <span style={{ minWidth: 24, textAlign: 'right' }}>{minRadius.toFixed(1)}</span>
            <label style={{ marginLeft: 16, marginRight: 4 }}>Big Dot Size</label>
            <input
              type="range"
              min={minRadius + 0.1}
              max="40"
              step="0.1"
              value={maxRadius}
              onChange={e => {
                const newMaxRadius = Math.max(parseFloat(e.target.value), minRadius + 0.1);
                setMaxRadius(newMaxRadius);
                // Live preview - redraw circles with new sizes
                if (circleGrid) {
                  drawCircles(circleGrid);
                }
              }}
              style={{ width: 60 }}
            />
            <span style={{ minWidth: 24, textAlign: 'right' }}>{maxRadius.toFixed(1)}</span>
          </div>
          
          <div className="recording-status">
            {isRecording && (
              <span className="status-recording">🔴 Recording...</span>
            )}
            {recordingMode && !isRecording && !recordingBlob && !capturedFrames.length && (
              <span className="status-ready">⚪ Recording in progress...</span>
            )}
            {!recordingMode && !isRecording && !recordingBlob && !capturedFrames.length && (
              <span className="status-ready">⚪ Press ⚫ to record</span>
            )}
            {recordingBlob && (
              <div className="download-options">
                <div className="download-ready-indicator">✅ Ready!</div>
                <button
                  className="btn btn-download"
                  onClick={downloadRecording}
                >
                  📥 MP4
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="glyph-page-container">
        <div className="glyph-page-content">
          <div className="glyph-display">
            {imageUrl ? (
              <div className="image-container">
                <img
                  ref={activeImageRef}
                  src={imageUrl}
                  alt="Uploaded image"
                  className="uploaded-image"
                  loading="lazy"
                  style={{
                    filter: !imageVisible ? 'grayscale(100%)' : 'none',
                    opacity: !imageVisible ? imageOpacity : (showImage ? 1 : 0),
                    transition: !imageVisible && imageOpacity === 0 ? 'none' : `opacity ${CROSSFADE_DURATION}ms ease-in, filter ${CROSSFADE_DURATION}ms ease-in`,
                  }}
                />
                <canvas
                  ref={canvasRef}
                  className="glyph-canvas"
                  style={canvasStyle}
                />
              </div>
            ) : (
              <div className="placeholder">
                <div className="placeholder-content">
                  <span>📷</span>
                  <p>Upload an image to get started</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
} 